import { motion } from 'framer-motion';
import Layout from '../components/Layout';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';

const PrivacyPage = () => {
  return (
    <Layout>
      {/* Hero */}
      <section className="bg-gradient-to-br from-slate-50 via-rose-50 to-slate-50 py-16">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <Badge className="mb-4">Legal</Badge>
            <h1 className="font-heading text-4xl md:text-5xl font-bold mb-4 text-slate-900">
              Privacy <span className="text-primary">Policy</span>
            </h1>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Please read our privacy policy carefully to understand how we protect your personal information.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 md:px-8">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.1 }}
          >
            <Card className="border-0 shadow-lg bg-slate-50/50 p-6 md:p-10">
              <CardContent className="prose prose-slate max-w-none space-y-8">
                <div>
                  <h2 className="font-heading text-2xl font-bold text-slate-900 mb-3">Privacy Policy</h2>
                  <p className="text-slate-600 leading-relaxed">
                    This Privacy Policy explains how Caliber Nova collects, uses, stores, and protects the information you provide when using our website, submitting inquiries, or enrolling in our educational programs. 
                    By using our website or services, you consent to the practices described in this Privacy Policy.
                  </p>
                </div>

                <div>
                  <h3 className="font-heading text-xl font-semibold text-slate-900 mb-2">Information We Collect</h3>
                  <p className="text-slate-600 leading-relaxed">
                    We may collect the following information from students, parents, and website visitors:
                  </p>
                  <ul className="list-disc pl-6 text-slate-600 space-y-1 mt-2">
                    <li>Name</li>
                    <li>Mobile Number</li>
                    <li>Email Address</li>
                    <li>Academic Information</li>
                    <li>Address and Location Details</li>
                    <li>Course Preferences</li>
                    <li>Any information submitted through inquiry or admission forms</li>
                  </ul>
                </div>

                <div>
                  <h3 className="font-heading text-xl font-semibold text-slate-900 mb-2">How We Use Your Information</h3>
                  <p className="text-slate-600 leading-relaxed">
                    The information collected may be used for:
                  </p>
                  <ul className="list-disc pl-6 text-slate-600 space-y-1 mt-2">
                    <li>Admission and enrollment processes</li>
                    <li>Student support and academic guidance</li>
                    <li>Course-related communication</li>
                    <li>Test schedules and important announcements</li>
                    <li>Responding to inquiries and requests</li>
                    <li>Improving our educational services</li>
                  </ul>
                  <p className="text-slate-500 italic mt-2 text-sm">
                    *We use your information only for legitimate educational and administrative purposes.
                  </p>
                </div>

                <div>
                  <h3 className="font-heading text-xl font-semibold text-slate-900 mb-2">Communication Consent</h3>
                  <p className="text-slate-600 leading-relaxed">
                    By submitting your contact details through our website, forms, or admission process, you consent to receive communication from Caliber Nova via:
                  </p>
                  <ul className="list-disc pl-6 text-slate-600 space-y-1 mt-2">
                    <li>Phone Calls</li>
                    <li>SMS</li>
                    <li>WhatsApp</li>
                    <li>Email</li>
                  </ul>
                  <p className="text-slate-600 mt-2 text-sm text-slate-500">
                    *These communications may include admission updates, course information, examination schedules, academic notifications, and student support services.
                  </p>
                </div>

                <div>
                  <h3 className="font-heading text-xl font-semibold text-slate-900 mb-2">Data Protection</h3>
                  <p className="text-slate-600 leading-relaxed">
                    We take reasonable measures to protect your personal information from unauthorized access, misuse, disclosure, or loss. 
                    While we strive to maintain the security of your data, no method of online transmission or electronic storage can be guaranteed as completely secure.
                  </p>
                </div>

                <div>
                  <h3 className="font-heading text-xl font-semibold text-slate-900 mb-2">Information Sharing</h3>
                  <p className="text-slate-600 leading-relaxed">
                    Caliber Nova does not sell, rent, or trade personal information to third parties. 
                    Information may only be shared:
                  </p>
                  <ul className="list-disc pl-6 text-slate-600 space-y-1 mt-2">
                    <li>When required by law or government authorities</li>
                    <li>To process payments through authorized payment providers</li>
                    <li>With service providers assisting in academic or administrative operations</li>
                  </ul>
                </div>

                <div>
                  <h3 className="font-heading text-xl font-semibold text-slate-900 mb-2">Cookies & Website Usage</h3>
                  <p className="text-slate-600 leading-relaxed">
                    Our website may use cookies and similar technologies to enhance user experience and improve website performance. 
                    Users may choose to disable cookies through their browser settings; however, some website features may not function properly.
                  </p>
                </div>

                <div>
                  <h3 className="font-heading text-xl font-semibold text-slate-900 mb-2">Third-Party Links</h3>
                  <p className="text-slate-600 leading-relaxed">
                    Our website may contain links to third-party websites for informational purposes. 
                    Caliber Nova is not responsible for the privacy practices, content, or policies of external websites.
                  </p>
                </div>

                <div>
                  <h3 className="font-heading text-xl font-semibold text-slate-900 mb-2">Student Records</h3>
                  <p className="text-slate-600 leading-relaxed">
                    Academic records, attendance information, test results, and other educational data may be maintained by Caliber Nova for academic, administrative, and regulatory purposes. 
                    Such records will be handled responsibly and confidentially.
                  </p>
                </div>

                <div>
                  <h3 className="font-heading text-xl font-semibold text-slate-900 mb-2">Policy Updates</h3>
                  <p className="text-slate-600 leading-relaxed">
                    Caliber Nova reserves the right to update this Privacy Policy at any time. 
                    Any changes will be posted on this page, and continued use of our website or services indicates acceptance of the updated policy.
                  </p>
                </div>

                <div className="pt-6 border-t border-slate-200">
                  <h3 className="font-heading text-xl font-semibold text-slate-900 mb-2">Contact Information</h3>
                  <p className="text-slate-700 font-semibold">Caliber Nova</p>
                  <ul className="space-y-1 text-slate-600 text-sm mt-2">
                    <li><strong>Email:</strong> info@calibernova.in</li>
                    <li><strong>Website:</strong> www.calibernova.in</li>
                    <li><strong>Address:</strong> 2nd Floor, Zenda Chowk, Mahal, Nagpur - 440032</li>
                    <li><strong>Phone:</strong> +91 9595253778 | +91 9527225500</li>
                  </ul>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </section>
    </Layout>
  );
};

export default PrivacyPage;
